import { ChatMessage } from "./messageHelpers";
import {
  ApprovalData,
  ApprovalQueueState,
  ApprovalProcessingResult,
  isValidApprovalId,
  hasApprovalData,
  isApprovalRequest,
  isApprovalResponse,
  safeExtractApprovalId,
  safeExtractToolName,
  handleApprovalError,
  ApprovalValidationError,
  ApprovalProcessingError,
} from "../types/approval";

/**
 * Enhanced Approval Helper Utilities
 *
 * Functions for managing sequential tool approval flow logic with comprehensive
 * null safety, validation, and error handling.
 */

/**
 * Safely validate messages array
 */
function validateMessages(messages: unknown): ChatMessage[] | null {
  try {
    if (!Array.isArray(messages)) return null;

    // Basic validation - ensure all items have required properties
    const validMessages = messages.filter(
      (msg): msg is ChatMessage =>
        msg &&
        typeof msg === "object" &&
        typeof msg.id === "string" &&
        typeof msg.role === "string" &&
        ["user", "assistant", "system", "tool"].includes(msg.role)
    );

    return validMessages.length === messages.length ? validMessages : null;
  } catch (error) {
    console.error("[validateMessages] Validation failed:", error);
    return null;
  }
}

/**
 * Safely get the last assistant message with null checks
 */
function getLastAssistantMessage(messages: ChatMessage[]): ChatMessage | null {
  try {
    const assistantMessages = messages.filter((m) => m.role === "assistant");
    return assistantMessages.length > 0
      ? assistantMessages[assistantMessages.length - 1]
      : null;
  } catch (error) {
    console.error("[getLastAssistantMessage] Error:", error);
    return null;
  }
}

/**
 * Safely extract approval parts with comprehensive validation
 */
function extractApprovalParts(
  message: ChatMessage,
  state: "approval-requested" | "approval-responded"
) {
  try {
    if (!message?.parts || !Array.isArray(message.parts)) {
      return [];
    }

    return message.parts.filter((part) => {
      try {
        return hasApprovalData(part) && part.state === state;
      } catch {
        return false;
      }
    });
  } catch (error) {
    console.error(
      `[extractApprovalParts] Error extracting ${state} parts:`,
      error
    );
    return [];
  }
}

/**
 * Check if we should auto-send after tool approvals are resolved (SEQUENTIAL VERSION)
 *
 * Enhanced version with comprehensive null safety and validation.
 * For sequential approval flow, we auto-send when:
 * 1. There is at least one approval response in the last message
 * 2. NO text content appears after the MOST RECENT approval response
 * 3. We're not in the middle of processing a sequential queue
 * 4. For sequential flow: We don't wait for ALL approvals - we process one at a time
 *
 * @param messages - Array of chat messages (with null safety)
 * @param queueState - Optional queue state for sequential processing
 * @returns true if conversation should auto-send, false otherwise
 */
export function shouldAutoSendAfterApprovals(
  messages: unknown,
  queueState?: ApprovalQueueState | null
): boolean {
  try {
    // Validate input messages
    const validMessages = validateMessages(messages);
    if (!validMessages || validMessages.length === 0) {
      console.log("[shouldAutoSend] Invalid or empty messages array");
      return false;
    }

    // For sequential flow, don't auto-send if queue is still processing
    if (queueState?.isProcessing) {
      console.log("[shouldAutoSend] Queue is processing, waiting...");
      return false;
    }

    // Get the last assistant message safely
    const lastAssistantMessage = getLastAssistantMessage(validMessages);
    if (!lastAssistantMessage?.parts) {
      console.log("[shouldAutoSend] No assistant message or parts");
      return false;
    }

    // Extract approval parts with validation
    const approvalParts = extractApprovalParts(
      lastAssistantMessage,
      "approval-requested"
    );
    const respondedParts = extractApprovalParts(
      lastAssistantMessage,
      "approval-responded"
    );

    // SEQUENTIAL LOGIC: Auto-send if we have ANY approval response and no text after it
    // We don't wait for ALL approvals to be resolved - process one at a time
    if (respondedParts.length === 0) {
      console.log("[shouldAutoSend] No approval responses yet");
      return false;
    }

    // Find the last index of approval-responded (search from end)
    let lastRespondedIdx = -1;
    try {
      for (let i = lastAssistantMessage.parts.length - 1; i >= 0; i--) {
        const part = lastAssistantMessage.parts[i];
        if (hasApprovalData(part) && part.state === "approval-responded") {
          lastRespondedIdx = i;
          break;
        }
      }
    } catch (error) {
      console.error(
        "[shouldAutoSend] Error finding last responded index:",
        error
      );
      lastRespondedIdx = -1;
    }

    // Check for text parts that come AFTER the last approval-responded
    let hasPostApprovalText = false;
    try {
      hasPostApprovalText = lastAssistantMessage.parts.some((part, idx) => {
        try {
          return (
            idx > lastRespondedIdx &&
            part?.type === "text" &&
            typeof part.text === "string" &&
            part.text.trim().length > 0
          );
        } catch {
          return false;
        }
      });
    } catch (error) {
      console.error(
        "[shouldAutoSend] Error checking post-approval text:",
        error
      );
      hasPostApprovalText = false;
    }

    // SEQUENTIAL: Auto-send if we have a response and no text after it
    // Don't wait for all pending approvals to be resolved
    const shouldSend = respondedParts.length > 0 && !hasPostApprovalText;

    console.log("[shouldAutoSend] Sequential check:", {
      approvalPending: approvalParts.length,
      approvalResponded: respondedParts.length,
      lastRespondedIdx,
      hasPostApprovalText,
      queueProcessing: queueState?.isProcessing ?? false,
      shouldSend,
      sequentialMode: true,
      lastAssistantParts: lastAssistantMessage.parts.map((p, idx) => ({
        idx,
        type: p?.type ?? "unknown",
        state: (p as any)?.state ?? "unknown",
        hasText:
          p?.type === "text" &&
          typeof p.text === "string" &&
          p.text.trim().length > 0,
        isAfterApproval: idx > lastRespondedIdx,
        hasApprovalData: hasApprovalData(p),
      })),
    });

    return shouldSend;
  } catch (error) {
    const approvalError = handleApprovalError(
      error,
      "shouldAutoSendAfterApprovals"
    );
    console.error("[shouldAutoSend] Critical error:", approvalError);
    return false; // Fail safe - don't auto-send on errors
  }
}

/**
 * Safely collect all approval IDs from messages with validation
 */
export function collectApprovalIds(messages: ChatMessage[]): string[] {
  try {
    const validMessages = validateMessages(messages);
    if (!validMessages) return [];

    const approvalIds = new Set<string>();

    for (const message of validMessages) {
      if (message.role !== "assistant" || !message.parts) continue;

      for (const part of message.parts) {
        try {
          const approvalId = safeExtractApprovalId(part);
          if (approvalId) {
            approvalIds.add(approvalId);
          }
        } catch (error) {
          console.warn(
            "[collectApprovalIds] Error extracting approval ID:",
            error
          );
          continue;
        }
      }
    }

    return Array.from(approvalIds);
  } catch (error) {
    const approvalError = handleApprovalError(error, "collectApprovalIds");
    console.error("[collectApprovalIds] Error:", approvalError);
    return [];
  }
}

/**
 * Safely find approval parts across all messages with comprehensive validation
 */
export function findApprovalParts(messages: ChatMessage[], approvalId: string) {
  try {
    if (!isValidApprovalId(approvalId)) {
      throw new ApprovalValidationError(
        "Invalid approval ID provided",
        approvalId
      );
    }

    const validMessages = validateMessages(messages);
    if (!validMessages) {
      throw new ApprovalValidationError("Invalid messages array");
    }

    const approvalParts: Array<{
      part: any;
      messageId: string;
      textParts: any[];
    }> = [];

    // DEBUG: Log all messages that contain this approval ID
    const messagesWithApproval = validMessages.filter(
      (msg) =>
        msg.role === "assistant" &&
        msg.parts?.some(
          (part) => hasApprovalData(part) && part.approval.id === approvalId
        )
    );

    if (messagesWithApproval.length > 1) {
      console.warn(
        `🚨 [findApprovalParts] DUPLICATE DETECTION: Approval ${approvalId} found in ${messagesWithApproval.length} messages:`,
        messagesWithApproval.map((m) => ({
          id: m.id,
          partsCount: m.parts?.length,
        }))
      );
    }

    for (const message of validMessages) {
      if (message.role !== "assistant" || !message.parts) continue;

      try {
        // Find tool parts with this approval ID
        const toolPart = message.parts.find((part) => {
          try {
            return hasApprovalData(part) && part.approval.id === approvalId;
          } catch {
            return false;
          }
        });

        if (toolPart) {
          // ENHANCED FIX: More precise text-to-approval matching
          // This prevents cross-contamination between different approvals
          const toolPartIndex = message.parts.indexOf(toolPart);

          console.log(
            `🔍 [findApprovalParts] Processing approval ${approvalId} in message ${message.id}:`,
            {
              approvalId,
              messageId: message.id,
              toolPartIndex,
              toolPartState: toolPart.state,
              totalParts: message.parts.length,
              toolName: toolPart.toolName || toolPart.tool || "unknown",
              isDuplicateMessage: messagesWithApproval.length > 1,
            }
          );

          // Find text parts that come AFTER this specific tool part
          const candidateTextParts = message.parts.filter((part, index) => {
            try {
              return (
                index > toolPartIndex && // Only text AFTER this tool part
                part?.type === "text" &&
                typeof part.text === "string" &&
                part.text.trim().length > 0
              );
            } catch {
              return false;
            }
          });

          console.log(
            `🔍 [findApprovalParts] Found ${candidateTextParts.length} candidate text parts after tool part ${toolPartIndex}`
          );

          // FIXED: Improved text association for AI SDK v6 multi-message flow
          // In multi-message scenarios, text often appears in the same message as approval-responded
          const shouldIncludeText =
            toolPart.state === "approval-responded" ||
            toolPart.state === "output-available" ||
            toolPart.state === "output-error";

          // ENHANCED: Better text association logic for sequential approvals
          let relevantTextParts: any[] = [];
          if (
            shouldIncludeText &&
            candidateTextParts.length > 0 &&
            message.parts
          ) {
            // Strategy 1: If this is approval-responded, include ALL text after it (likely success message)
            if (toolPart.state === "approval-responded") {
              relevantTextParts = candidateTextParts;
            } else {
              // Strategy 2: For other states, use the original selective logic
              let nextToolPartIndex = -1;
              for (let i = toolPartIndex + 1; i < message.parts.length; i++) {
                const part = message.parts[i];
                if (
                  part?.type?.startsWith("tool-") &&
                  (part as any).approval?.id
                ) {
                  nextToolPartIndex = i;
                  break;
                }
              }

              relevantTextParts = candidateTextParts.filter((part) => {
                const actualIndex = message.parts!.indexOf(part);
                return (
                  nextToolPartIndex === -1 || actualIndex < nextToolPartIndex
                );
              });
            }
          }

          console.log(
            `🔍 [findApprovalParts] Final result for approval ${approvalId}:`,
            {
              shouldIncludeText,
              candidateTextParts: candidateTextParts.length,
              relevantTextParts: relevantTextParts.length,
              textPreview: relevantTextParts
                .map((p) => (p as any).text?.substring(0, 30) + "...")
                .join(" | "),
            }
          );

          approvalParts.push({
            part: toolPart,
            messageId: message.id,
            textParts: relevantTextParts,
          });
        }
      } catch (error) {
        console.warn(
          `[findApprovalParts] Error processing message ${message.id}:`,
          error
        );
        continue;
      }
    }

    return approvalParts;
  } catch (error) {
    const approvalError = handleApprovalError(error, "findApprovalParts");
    console.error("[findApprovalParts] Error:", approvalError);
    return [];
  }
}

/**
 * Get the most advanced state for an approval with comprehensive error handling
 */
export function getApprovalState(messages: ChatMessage[], approvalId: string) {
  try {
    if (!isValidApprovalId(approvalId)) {
      throw new ApprovalValidationError("Invalid approval ID", approvalId);
    }

    const approvalParts = findApprovalParts(messages, approvalId);
    if (approvalParts.length === 0) {
      return null;
    }

    // Priority: Find most advanced state
    // 1. If any message has text content → success/error state
    const withText = approvalParts.find((a) => a.textParts.length > 0);
    if (withText) {
      const successText = withText.textParts
        .map((p) => p.text)
        .filter((text) => typeof text === "string")
        .join("\n\n");

      return {
        state: "success",
        successText,
        part: withText.part,
        messageId: withText.messageId,
      };
    }

    // 2. If approval-responded → executing state
    const responded = approvalParts.find(
      (a) => a.part?.state === "approval-responded"
    );
    if (responded) {
      return {
        state: "executing",
        part: responded.part,
        messageId: responded.messageId,
      };
    }

    // 3. If approval-requested → requesting state
    const requested = approvalParts.find(
      (a) => a.part?.state === "approval-requested"
    );
    if (requested) {
      return {
        state: "requesting",
        part: requested.part,
        messageId: requested.messageId,
      };
    }

    // 4. Output error state
    const errored = approvalParts.find((a) => a.part?.state === "output-error");
    if (errored) {
      return {
        state: "error",
        errorText: errored.part?.errorText || "Tool execution failed",
        part: errored.part,
        messageId: errored.messageId,
      };
    }

    return null;
  } catch (error) {
    const approvalError = handleApprovalError(error, "getApprovalState");
    console.error("[getApprovalState] Error:", approvalError);
    return null;
  }
}
